import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  Tooltip,
  CircularProgress,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Alert,
  useTheme,
  alpha,
} from "@mui/material";
import DynamicTable from "../../../data/DynamicTable/dynamicTable";
import {
  companyManagementUrl,
  salesManagement,
  AccountsandFinanceurl,
} from "../../../config";
import {
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PendingActions as PendingActionsIcon,
  Storefront as StorefrontIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  allCompanyBranchesView,
  AllVendors,
  PaymentMethodQuery,
} from "../../../data/Axios/queries";
import PaymentVoucherForm from "./form";
import {
  graphqlQuery,
  fetchDataEngine,
} from "../../../data/Axios/DynamicService";
import {
  PAYMENT_VOUCHER_STATISTICS_QUERY,
  PAYMENT_VOUCHERS_PAGINATED_QUERY,
  SEARCH_PAYMENT_VOUCHERS_QUERY,
} from "./paymentvoucherqueries";
import { tokens } from "../../../theme";

function PaymentVoucher() {
  const navigate = useNavigate();
  const base_url = salesManagement.uri;
  const finance_url = AccountsandFinanceurl.uri;

  // ✅ Read token once at the top
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const companyId = decodedToken?.CompanyId
    ? parseInt(decodedToken.CompanyId)
    : null;
  const tokenBranchId = parseInt(decodedToken?.BranchId);

  // ✅ User is branch-locked when branchId is a valid positive number
  const isBranchLocked = !isNaN(tokenBranchId) && tokenBranchId > 0;


  // State
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState("");

  // Pagination - 0-based for MUI DataGrid
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);

  // Filter Data Options
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);

  // Search: separate input state vs committed query
  const [searchInput, setSearchInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("");
  // ✅ Default branchFilter to locked branchId if applicable, otherwise "all"
  const [branchFilter, setBranchFilter] = useState(
    isBranchLocked ? String(tokenBranchId) : "all"
  );
  const [vendorFilter, setVendorFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");

  // Statistics
  const [stats, setStats] = useState({
    totalVouchers: 0,
    totalAmount: 0,
    paidVouchers: 0,
    initialStagesVouchers: 0,
    reversedVouchers: 0,
    rejectedVouchers: 0,
    averageVoucherValue: 0,
  });

  // Ref to prevent multiple simultaneous requests
  const fetchInProgress = useRef(false);

  // Ref that always holds latest filter values
  const filtersRef = useRef({});
  filtersRef.current = {
    page,
    pageSize,
    searchQuery,
    dateFrom,
    dateTo,
    paymentMethodFilter,
    branchFilter,
    vendorFilter,
    paymentStatusFilter,
  };

  // MUI theme-aware colors
  const muiTheme = useTheme();
  const colors = tokens(muiTheme.palette.mode);
  const isDark = muiTheme.palette.mode === "dark";

  const theme = {
    primary: isDark ? colors.blueAccent[400] : "#1976D2",
    secondary: isDark ? colors.blueAccent[500] : "#0288D1",
    success: "#4CAF50",
    warning: "#FF9800",
    error: "#F44336",
    lightBg: isDark ? alpha(colors.blueAccent[400], 0.15) : "#E3F2FD",
    cardBg: isDark ? colors.primary[400] : "#FFFFFF",
    pageBg: isDark ? colors.primary[500] : "#F5F7FA",
    iconBadgeGreen: isDark ? alpha("#4CAF50", 0.15) : "#E8F5E9",
    lockedBg: isDark ? alpha(colors.blueAccent[400], 0.08) : "#F0F7FF",
  };

  const actions = {
    entity: "PurchaseOrderItems",
    isSubRight: true,
    editpaymentvoucher: {
      key: "EDIT",
      button_name: "Edit",
      Show_Button: true,
      permission: "EditPermission",
    },
    add: {
      key: "ADD",
      button_name: "Add",
      Show_Button: false,
      permission: "CreatePermission",
    },
    showMore: {
      key: "SHOWMORE",
      button_name: "Show More",
      Show_Button: true,
      permission: "EditPermission",
      title: "Payment Voucher Details",
    },
    approveddisapprovedpv: {
      key: "ACTIVATEDEACTIVATE",
      button_name: "Approve",
      inputObjectName: "approvePaymentVoucher",
      inputSubObjectName: "approvePaymentVoucher",
      Show_Button: true,
      permission: "ActivatePermission",
      modelName: "id",
    },
    disapprovedpv: {
      key: "ACTIVATEDEACTIVATE",
      button_name: "DisApprove",
      inputObjectName: "approvePaymentVoucher",
      inputSubObjectName: "approvePaymentVoucher",
      Show_Button: true,
      permission: "ActivatePermission",
      modelName: "id",
    },
    reversePaymentVoucher: {
      key: "ACTIVATEDEACTIVATE",
      button_name: "Reverse",
      inputObjectName: "reversePaymentVoucher",
      inputSubObjectName: "reversePaymentVoucher",
      Show_Button: true,
      permission: "ActivatePermission",
      modelName: "id",
    },
    returnFields: ["id", "createdBy"],
    showStatus: true,
    pvPaymentStatus: true,
  };

  const ignore = [
    "paymentAccountId",
    "paymentMethod",
    "vendorId",
    "companyId",
    "updatedBy",
    "createdBy",
    "creator",
    "approver",
    "approvedBy",
    "approvedDate",
    "updatedDate",
    "updator",
    "rejectedBy",
    "rejectedDate",
    "reference",
    "reason",
    "actionBy",
    "actionedDate",
    "totalRecords",
    "currentPage",
    "pageSize",
    "totalPages",
    "firstRecordInPage",
    "lastRecordInPage",
  ];

  const unignoredIds = ["purchaseOrderId", "id", "paymentVoucherId"];

  const ignoreShowMore = [
    "paymentAccountId",
    "vendorId",
    "companyId",
    "updatedBy",
    "createdBy",
    "updatedDate",
    "formattedDate",
    "rejectedBy",
    "rejectedDate",
    "totalPages",
    "totalRecords",
    "pageSize",
    "lastRecordInPage",
    "currentPage",
    "firstRecordInPage",
    "approvedBy",
    "actionBy",
    "actionedDate",
  ];

  // ─── Debounce search input ────────────────────────────────────────────────────
  useEffect(() => {
    setIsTyping(true);
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(0);
      setIsTyping(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ─── Lookup helpers ───────────────────────────────────────────────────────────
  const fetchPaymentMethods = async () => {
    try {
      const methodsRes = await graphqlQuery(PaymentMethodQuery, finance_url);
      if (methodsRes) {
        setPaymentMethods(Array.isArray(methodsRes) ? methodsRes : []);
      }
    } catch (err) {
      console.error("Error fetching payment methods:", err);
    }
  };

  // ✅ Fetch branches only when user is NOT branch-locked
  const fetchBranches = async () => {
    try {
      const response = await graphqlQuery(
        allCompanyBranchesView,
        companyManagementUrl.uri
      );
      setBranches(response || []);
    } catch (err) {
      console.error("Error fetching branches:", err);
      setBranches([]);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await graphqlQuery(AllVendors, base_url);
      setVendors(response || []);
    } catch (err) {
      setVendors([]);
    }
  };

  const fetchStatistics = async () => {
    try {
      setLoadingStats(true);
      const response = await graphqlQuery(
        PAYMENT_VOUCHER_STATISTICS_QUERY,
        base_url
      );
      if (response && response[0]) {
        const s = response[0];
        setStats({
          totalVouchers: s.totalVouchers || 0,
          totalAmount: s.totalAmount || 0,
          paidVouchers: s.paidVouchers || 0,
          initialStagesVouchers: s.initialStagesVouchers || 0,
          reversedVouchers: s.reversedVouchers || 0,
          rejectedVouchers: s.rejectedVouchers || 0,
          averageVoucherValue: s.averageVoucherValue || 0,
        });
      }
    } catch (err) {
      console.error("Error fetching statistics:", err);
    } finally {
      setLoadingStats(false);
    }
  };

  const calculateStatsFromData = (data) => {
    const totalVouchers = data.length;
    const totalAmount = data.reduce((sum, v) => sum + (v.paidAmount || 0), 0);
    const paidVouchers = data.filter((v) => v.isActive === 1).length;
    const initialStagesVouchers = data.filter((v) => v.isActive === 0).length;
    const rejectedVouchers = data.filter((v) => v.isActive === 3).length;
    const reversedVouchers = data.filter((v) => v.isActive === 5).length;
    const averageVoucherValue =
      totalVouchers > 0 ? totalAmount / totalVouchers : 0;

    setStats({
      totalVouchers,
      totalAmount,
      paidVouchers,
      initialStagesVouchers,
      reversedVouchers,
      rejectedVouchers,
      averageVoucherValue,
    });
  };
  
const lockedBranchName =
  isBranchLocked
      ? `${branches.find((b) => b.id === parseInt(tokenBranchId)) ?.branchName }`
      : branches.find((b) => b.id === parseInt(branchFilter) )?.branchName || "Unknown";

  // ─── Active filters — branch excluded from count/check when locked ────────────
  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        searchQuery ||
          dateFrom ||
          dateTo ||
          paymentMethodFilter ||
          // ✅ Branch only counts as an active filter when user can change it
          (!isBranchLocked && branchFilter !== "all") ||
          vendorFilter !== "all" ||
          paymentStatusFilter !== "all"
      ),
    [
      searchQuery,
      dateFrom,
      dateTo,
      paymentMethodFilter,
      isBranchLocked,
      branchFilter,
      vendorFilter,
      paymentStatusFilter,
    ]
  );

  // ─── STABLE fetch function ────────────────────────────────────────────────────
  const fetchVouchers = useCallback(async () => {
    if (fetchInProgress.current) {
      console.log("Fetch already in progress, skipping...");
      return;
    }

    const {
      page,
      pageSize,
      searchQuery,
      dateFrom,
      dateTo,
      paymentMethodFilter,
      branchFilter,
      vendorFilter,
      paymentStatusFilter,
    } = filtersRef.current;

    // ✅ Branch filter is always active when locked, but not counted as a
    // "user-applied" filter for the hasActiveFilters banner
    const activeFilters = Boolean(
      searchQuery ||
        dateFrom ||
        dateTo ||
        paymentMethodFilter ||
        branchFilter !== "all" || // includes locked branch — still filters data
        vendorFilter !== "all" ||
        paymentStatusFilter !== "all"
    );

    setLoading(true);
    setError("");
    fetchInProgress.current = true;

    try {
      let vouchersData = [];
      let total = 0;
      let response;

      if (activeFilters) {
        const variables = {
          searchParam: searchQuery ?? null,
          startDate: dateFrom ? `${dateFrom}T00:00:00.000` : null,
          endDate: dateTo ? `${dateTo}T23:59:59.999` : null,
          pageNumber: page + 1,
          pageSize,
          vendorId: vendorFilter !== "all" ? parseInt(vendorFilter) : null,
          branchid: branchFilter !== "all" ? parseInt(branchFilter) : null,
          paymentMethodId: paymentMethodFilter || null,
          paymentStatus:
            paymentStatusFilter !== "all"
              ? parseInt(paymentStatusFilter)
              : null,
        };

        const returnFields = [
          "paymentVoucherId",
          "purchaseOrderId",
          "vendorId",
          "vendorName",
          "paymentMethod",
          "methodName",
          "paymentAccountId",
          "accountName",
          "paidAmount",
          "outstandingBalance",
          "paymentDate",
          "reference",
          "remarks",
          "isActive",
          "createdBy",
          "creator",
          "approvedBy",
          "approver",
          "createdDate",
          "approvedDate",
          "updatedDate",
          "updatedBy",
          "updator",
          "companyId",
          "rejectedBy",
          "rejectedDate",
          "branchName",
          "reason",
          "actionBy",
          "actionedDate",
          "reversedBy",
          "reversedDate"
        ];

        response = await fetchDataEngine(
          "allPaymentVouchersView",
          "paymentVoucherFilter",
          variables,
          returnFields,
          base_url
        );

        vouchersData = Array.isArray(response)
          ? response
          : response?.searchPaymentVouchersFromView ?? [];
      } else {
        response = await graphqlQuery(
          PAYMENT_VOUCHERS_PAGINATED_QUERY,
          base_url,
          { pageNumber: page + 1, pageSize }
        );

        vouchersData =
          response?.data?.paymentVouchersPaginatedFunc ??
          response?.[0]?.paymentVouchersPaginatedFunc ??
          response?.paymentVouchersPaginatedFunc ??
          (Array.isArray(response) ? response : []);
      }

      if (vouchersData.length > 0) {
        total = vouchersData[0].totalRecords || vouchersData.length;
      }

      const formattedVouchers = vouchersData.map((voucher) => ({
        ...voucher,
        id: voucher.paymentVoucherId,
        formattedDate: voucher.paymentDate
          ? format(new Date(voucher.paymentDate), "yyyy-MM-dd")
          : "",
      }));

      setVouchers(formattedVouchers);
      setRowCount(total);
      if (activeFilters) {
        calculateStatsFromData(formattedVouchers);
      }
    } catch (err) {
      console.error("Error fetching payment vouchers:", err);
      setError("Failed to load payment vouchers. Please try again.");
    } finally {
      setLoading(false);
      fetchInProgress.current = false;
    }
  }, [base_url]);

  // ─── Effect 1: trigger fetch when any filter or page changes ─────────────────
  useEffect(() => {
    fetchVouchers();
  }, [
    fetchVouchers,
    page,
    pageSize,
    searchQuery,
    dateFrom,
    dateTo,
    paymentMethodFilter,
    branchFilter,
    vendorFilter,
    paymentStatusFilter,
  ]);

  // ─── Effect 2: reset page + restore real stats when all filters cleared ───────
  useEffect(() => {
    if (!hasActiveFilters) {
      if (page !== 0) setPage(0);
      fetchStatistics();
    }
  }, [hasActiveFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Mount-only: load dropdowns and statistics ────────────────────────────────
  useEffect(() => {
    fetchPaymentMethods();
    fetchBranches(); // skips internally when isBranchLocked
    fetchVendors();
    fetchStatistics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Derived options ──────────────────────────────────────────────────────────
  const branchOptions = branches.map((branch) => ({
    id: branch.id,
    name: branch.branchName,
  }));

  const vendorOptions = vendors.map((vendor) => ({
    id: vendor.id,
    name: vendor.vendorName,
  }));

  // ─── Handlers ────────────────────────────────────────────────────────────────
  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  // ✅ Clear filters — branch is NOT reset when user is locked
  const handleClearFilters = () => {
    setSearchInput("");
    setSearchQuery("");
    setDateFrom("");
    setDateTo("");
    setPaymentMethodFilter("");
    if (!isBranchLocked) setBranchFilter("all"); // only reset if not locked
    setVendorFilter("all");
    setPaymentStatusFilter("all");
    setPage(0);
  };

  const handleRefresh = () => {
    fetchVouchers();
    fetchPaymentMethods();
    fetchBranches();
    fetchVendors();
  };

  const getPaymentMethodName = (id) => {
    const m = paymentMethods.find((pm) => String(pm.id) === String(id));
    return m ? m.accountName : id;
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, bgcolor: theme.pageBg, minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <AccountBalanceIcon
            sx={{ fontSize: 32, color: theme.primary, mr: 1 }}
          />
          <Typography variant="h4" fontWeight="700" color={theme.primary}>
            Payment Voucher Management
          </Typography>
      
        </Box>
        <Typography variant="body2" color="text.secondary">
          {isBranchLocked
            ? `Showing payment vouchers for your branch: ${lockedBranchName}`
            : "Track and manage all payment vouchers and vendor payments"}
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Total Amount */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: isDark
                ? `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`
                : `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
              color: "white",
              border: isDark
                ? `1px solid ${alpha(colors.blueAccent[400], 0.4)}`
                : "none",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                width: "150px",
                height: "150px",
                background: "rgba(255,255,255,0.1)",
                borderRadius: "50%",
                top: "-50px",
                right: "-50px",
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <AttachMoneyIcon sx={{ fontSize: 28, mr: 1 }} />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Total Amount
                </Typography>
                {loadingStats && (
                  <CircularProgress size={16} sx={{ ml: 1, color: "white" }} />
                )}
              </Box>
              <Typography variant="h4" fontWeight="700">
                KSh{" "}
                {stats.totalAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, mt: 1 }}>
                {stats.totalVouchers} vouchers • Avg: KSh{" "}
                {stats.averageVoucherValue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Status */}
        <Grid item xs={12} sm={6} md={9}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Payment Status
                    {isBranchLocked && (
                      <Chip
                        label={lockedBranchName}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ ml: 1, height: 18, fontSize: "0.65rem" }}
                      />
                    )}
                  </Typography>
                  <Box
                    sx={{ display: "flex", gap: 3, mt: 1, flexWrap: "wrap" }}
                  >
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight="700"
                        color={theme.success}
                      >
                        {stats.paidVouchers}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Paid
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography variant="h5" fontWeight="700" color="primary">
                        {stats.initialStagesVouchers}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Initial Stages
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography variant="h5" fontWeight="700" color="grey">
                        {stats.reversedVouchers}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Reversed
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight="700"
                        color={theme.error}
                      >
                        {stats.rejectedVouchers}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Rejected
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    bgcolor: theme.iconBadgeGreen,
                    p: 1.5,
                    borderRadius: 2,
                  }}
                >
                  <AccountBalanceIcon
                    sx={{ color: theme.success, fontSize: 28 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter Section */}
      <Paper
        elevation={0}
        sx={{ p: 2.5, mb: 3, borderRadius: 2, bgcolor: theme.cardBg }}
      >
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <FilterListIcon sx={{ color: theme.primary }} />
          <Typography variant="h6" fontWeight="600">
            Filters
          </Typography>
          {hasActiveFilters && (
            <Chip
              label={`${
                [
                  searchQuery && "Search",
                  dateFrom && "From",
                  dateTo && "To",
                  paymentMethodFilter && "Method",
                  // ✅ Branch not counted when locked
                  !isBranchLocked && branchFilter !== "all" && "Branch",
                  vendorFilter !== "all" && "Vendor",
                  paymentStatusFilter !== "all" && "Status",
                ].filter(Boolean).length
              } Active`}
              size="small"
              color="primary"
              onDelete={handleClearFilters}
            />
          )}
        </Box>

        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search ID, Payee, Vendor, Account..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {isTyping ? (
                      <CircularProgress
                        size={18}
                        sx={{ color: "text.secondary" }}
                      />
                    ) : (
                      <SearchIcon sx={{ color: "text.secondary" }} />
                    )}
                  </InputAdornment>
                ),
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchInput("");
                        setSearchQuery("");
                        setPage(0);
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>

          {/* Branch Filter — locked indicator OR dropdown */}
          {isBranchLocked ? (
            // ✅ Read-only locked branch display
            <Grid item xs={12} sm={6} md={2}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: theme.lockedBg,
                  height: 40,
                }}
              >
                <LockIcon sx={{ fontSize: 16, color: theme.primary }} />
                <StorefrontIcon sx={{ fontSize: 16, color: theme.primary }} />
                <Typography
                  variant="body2"
                  color={theme.primary}
                  fontWeight="600"
                  noWrap
                >
                  {lockedBranchName}
                </Typography>
              </Box>
            </Grid>
          ) : (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select
                  value={branchFilter}
                  label="Branch"
                  onChange={(e) => {
                    setBranchFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">All Branches</MenuItem>
                  {branchOptions.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <StorefrontIcon
                          sx={{ fontSize: 18, color: theme.primary }}
                        />
                        {branch.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Vendor Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Vendor</InputLabel>
              <Select
                value={vendorFilter}
                label="Vendor"
                onChange={(e) => {
                  setVendorFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Vendors</MenuItem>
                {vendorOptions.map((vendor) => (
                  <MenuItem key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Payment Method */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Account Name</InputLabel>
              <Select
                value={paymentMethodFilter}
                label="Account Name"
                onChange={(e) => {
                  setPaymentMethodFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All Methods</MenuItem>
                {paymentMethods.map((method) => (
                  <MenuItem key={method.id} value={method.id}>
                    {method.accountName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Payment Status */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={paymentStatusFilter}
                label="Payment Status"
                onChange={(e) => {
                  setPaymentStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="1">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleIcon
                      sx={{ fontSize: 18, color: theme.success }}
                    />
                    Paid
                  </Box>
                </MenuItem>
                <MenuItem value="0">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PendingActionsIcon
                      sx={{ fontSize: 18, color: "primary" }}
                    />
                    Initial Stages
                  </Box>
                </MenuItem>
                <MenuItem value="5">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CancelIcon sx={{ fontSize: 18, color: "grey" }} />
                    Reversed
                  </Box>
                </MenuItem>
                <MenuItem value="3">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CancelIcon sx={{ fontSize: 18, color: theme.error }} />
                    Rejected
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Date From */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(0);
              }}
            />
          </Grid>

          {/* Date To */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(0);
              }}
            />
          </Grid>

          {/* Refresh */}
          <Grid item xs={12} md={2} sx={{ ml: "auto" }}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading || loadingStats}
                  sx={{
                    bgcolor: theme.lightBg,
                    "&:hover": { bgcolor: theme.primary, color: "white" },
                  }}
                >
                  {loading || loadingStats ? (
                    <CircularProgress size={20} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>

        {/* Active Filter Chips */}
        {hasActiveFilters && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {searchQuery && (
              <Chip
                label={`Search: "${searchQuery}"`}
                size="small"
                onDelete={() => {
                  setSearchInput("");
                  setSearchQuery("");
                  setPage(0);
                }}
                color="primary"
                variant="outlined"
              />
            )}
            {/* ✅ Branch chip only shown when NOT locked */}
            {!isBranchLocked && branchFilter !== "all" && (
              <Chip
                label={`Branch: ${
                  branchOptions.find(
                    (b) => String(b.id) === String(branchFilter)
                  )?.name || branchFilter
                }`}
                size="small"
                onDelete={() => {
                  setBranchFilter("all");
                  setPage(0);
                }}
                color="primary"
                variant="outlined"
              />
            )}
            {vendorFilter !== "all" && (
              <Chip
                label={`Vendor: ${
                  vendorOptions.find(
                    (v) => String(v.id) === String(vendorFilter)
                  )?.name || vendorFilter
                }`}
                size="small"
                onDelete={() => {
                  setVendorFilter("all");
                  setPage(0);
                }}
                color="primary"
                variant="outlined"
              />
            )}
            {paymentMethodFilter && (
              <Chip
                label={`Account: ${getPaymentMethodName(paymentMethodFilter)}`}
                size="small"
                onDelete={() => {
                  setPaymentMethodFilter("");
                  setPage(0);
                }}
                color="primary"
                variant="outlined"
              />
            )}
            {paymentStatusFilter !== "all" && (
              <Chip
                label={`Status: ${
                  paymentStatusFilter === "1"
                    ? "Paid"
                    : paymentStatusFilter === "0"
                    ? "Initial Stages"
                    : paymentStatusFilter === "5"
                    ? "Reversed"
                    : paymentStatusFilter === "3"
                    ? "Rejected"
                    : ""
                }`}
                size="small"
                onDelete={() => {
                  setPaymentStatusFilter("all");
                  setPage(0);
                }}
                color="primary"
                variant="outlined"
              />
            )}
            {dateFrom && (
              <Chip
                label={`From: ${dateFrom}`}
                size="small"
                onDelete={() => {
                  setDateFrom("");
                  setPage(0);
                }}
                color="primary"
                variant="outlined"
              />
            )}
            {dateTo && (
              <Chip
                label={`To: ${dateTo}`}
                size="small"
                onDelete={() => {
                  setDateTo("");
                  setPage(0);
                }}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: theme.cardBg,
          position: "relative",
        }}
      >
        {/* Loading overlay */}
        {(loading || isTyping) && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              bgcolor: alpha(theme.cardBg, 0.75),
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              backdropFilter: "blur(2px)",
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              {isTyping ? "Searching…" : "Loading vouchers…"}
            </Typography>
          </Box>
        )}

        <DynamicTable
          title="Payment Voucher"
          subtitle={`Showing ${vouchers.length} of ${rowCount} vouchers${
            hasActiveFilters ? " (filtered)" : ""
          }`}
          rows={vouchers}
          rowCount={rowCount}
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          paginationMode="server"
          pagination={true}
          ignoreFields={ignore}
          unignoredIds={unignoredIds}
          ignoreShowMore={ignoreShowMore}
          query={
            hasActiveFilters
              ? SEARCH_PAYMENT_VOUCHERS_QUERY
              : PAYMENT_VOUCHERS_PAGINATED_QUERY
          }
          filteredData={vouchers}
          isfilteredData={true}
          FormComponent={PaymentVoucherForm}
          base_url={base_url}
          actions={actions}
          onServerAction={handleRefresh}
          onAction={handleRefresh}
          getRowId={(row) => row.paymentVoucherId || row.id}
        />
      </Paper>
    </Box>
  );
}

export default PaymentVoucher;